import type { TopCustomer } from "@/apis/gql/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Users } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, XAxis, YAxis } from "recharts";

interface TopCustomersChartProps {
  data: TopCustomer[];
  currency?: string;
}

const chartConfig = {
  totalAmount: {
    label: "Transaktionsvolumen",
    color: "hsl(var(--chart-4))",
  },
} satisfies ChartConfig;

export function TopCustomersChart({
  data,
  currency = "EUR",
}: TopCustomersChartProps) {
  const chartData = data.map((customer) => ({
    ...customer,
    totalAmount: customer.totalAmount,
  }));

  // Use a gradient of blue colors
  const getBarColor = (index: number) => {
    const colors = [
      "hsl(217, 91%, 60%)", // Bright blue
      "hsl(217, 85%, 55%)",
      "hsl(217, 80%, 50%)",
      "hsl(217, 75%, 45%)",
      "hsl(217, 70%, 40%)",
      "hsl(217, 65%, 38%)",
      "hsl(217, 60%, 36%)",
      "hsl(217, 55%, 34%)",
      "hsl(217, 50%, 32%)",
      "hsl(217, 45%, 30%)", // Darker blue
    ];
    return colors[index % colors.length];
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div>
            <CardTitle className="text-xl font-semibold">
              Top 10 Kunden
            </CardTitle>
            <CardDescription className="mt-0.5">
              Nach Umsatz sortiert
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[400px] text-muted-foreground">
            Keine Daten für den ausgewählten Zeitraum verfügbar
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[400px] w-full">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ left: 20, right: 20 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                className="stroke-muted/30"
              />
              <XAxis
                type="number"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) =>
                  new Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency,
                    notation: "compact",
                    compactDisplay: "short",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(value)
                }
                className="text-sm"
                tick={{ fill: "hsl(var(--muted-foreground))" }}
              />
              <YAxis
                type="category"
                dataKey="entityName"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                width={200}
                className="text-xs"
                tick={{ fill: "hsl(var(--foreground))" }}
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    indicator="line"
                    labelFormatter={(label) => label}
                    formatter={(value, name) => {
                      const numValue = Number(value);
                      return [
                        new Intl.NumberFormat("de-DE", {
                          style: "currency",
                          currency,
                        }).format(numValue),
                        chartConfig.totalAmount.label,
                      ];
                    }}
                  />
                }
              />
              <Bar dataKey="totalAmount" radius={[0, 6, 6, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={entry.entityId || `cell-${index}`}
                    fill={getBarColor(index)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
